import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { createRoute } from '../services/routeService';
import { CheckCircle, MapPin, Truck, AlertCircle, Navigation, Users, Package, Calendar, GripVertical, User, Plus, Lock, Printer, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { printDispatchRouteSheet } from '../utils/printRouteSheet';
import GeocodingAlertsPopup from '../components/GeocodingAlertsPopup';
import { DetailedOrdersTable } from '../components/DetailedOrdersTable';
import AdditionalStopsModal from '../components/AdditionalStopsModal';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const RouteCreationPage = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('pending_handoff');

  // Data state
  const [dispatchData, setDispatchData] = useState({
    unassigned: { count: 0, orders: [] },
    pending_handoff: { count: 0, by_messenger: [] },
    in_delivery: { count: 0, by_date: [] },
    delivered: { count: 0, filters: {}, by_messenger: [] }
  });

  // Legacy state (for Tab 1)
  const [messengers, setMessengers] = useState([]);
  const [selectedMessenger, setSelectedMessenger] = useState('');
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [routeDate, setRouteDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizedRoute, setOptimizedRoute] = useState(null);
  const [geocodingAlerts, setGeocodingAlerts] = useState(null);

  // Filters state for Tab 4 (delivered routes)
  const [deliveredFilters, setDeliveredFilters] = useState({
    date_from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    date_to: new Date().toISOString().split('T')[0],
    messenger_id: ''
  });

  // State for handoff checkboxes (Tab 2)
  const [selectedHandoffOrders, setSelectedHandoffOrders] = useState({});

  // State for Additional Stops Modal
  const [showStopsModal, setShowStopsModal] = useState(false);
  const [selectedRouteForStops, setSelectedRouteForStops] = useState(null);

  useEffect(() => {
    loadDispatchData();
    loadMessengers();
  }, []);

  const loadDispatchData = async (filters = {}) => {
    try {
      setLoading(true);

      // Aplicar filtros de Tab 4 si existen
      const queryParams = new URLSearchParams();
      if (filters.date_from) queryParams.append('date_from', filters.date_from);
      if (filters.date_to) queryParams.append('date_to', filters.date_to);
      if (filters.messenger_id) queryParams.append('messenger_id', filters.messenger_id);

      const response = await api.get(`/logistics/dispatch-overview?${queryParams.toString()}`);

      if (response.data.success) {
        setDispatchData(response.data.data);
        console.log('📊 Dispatch data loaded:', response.data.data);
      }
    } catch (err) {
      console.error('Error cargando vista de despacho:', err);
      toast.error('Error cargando datos de despacho');
    } finally {
      setLoading(false);
    }
  };

  const loadMessengers = async () => {
    try {
      // Pedir explícitamente solo mensajeros activos y aumentar el límite para evitar paginación
      // Esto también permite que los usuarios con rol 'logistica' puedan consultar la lista
      const usersResponse = await api.users.getUsers({ 
        role: 'mensajero', 
        active: true, 
        limit: 1000 
      });

      // El backend devuelve { success: true, data: { users: [], pagination: {} } }
      // api.js ya retorna response.data, por lo que usersResponse es el body.
      const activeMessengers = (usersResponse.data?.users || []).filter(
        u => u.role === 'mensajero' && u.active
      );
      
      setMessengers(activeMessengers);
    } catch (err) {
      console.error('Error cargando mensajeros:', err);
    }
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const handleSelectAll = () => {
    const orders = dispatchData.unassigned.orders;
    if (selectedOrders.length === orders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders.map(o => o.id));
    }
  };

  const handleOptimizeRoute = async () => {
    if (!selectedMessenger) {
      toast.error('Selecciona un mensajero');
      return;
    }

    if (selectedOrders.length === 0) {
      toast.error('Selecciona al menos un pedido');
      return;
    }

    try {
      setOptimizing(true);
      const selectedOrderData = dispatchData.unassigned.orders.filter(o => selectedOrders.includes(o.id));

      const response = await api.post('/routes/optimize', {
        orders: selectedOrderData.map(o => ({
          id: o.id,
          address: o.customer_address,
          city: o.customer_city || 'Medellín'
        }))
      });

      if (response.data.success) {
        setOptimizedRoute(response.data.data);

        if (response.data.data.geocoding_alerts && response.data.data.geocoding_alerts.length > 0) {
          setGeocodingAlerts(response.data.data.geocoding_alerts);
        }

        toast.success('Ruta optimizada correctamente');
      }
    } catch (err) {
      console.error('Error optimizando ruta:', err);
      toast.error('Error al optimizar la ruta');
    } finally {
      setOptimizing(false);
    }
  };

  const handleReorderRoute = (newOrderIds) => {
    // Update optimized route with new order
    setOptimizedRoute(prev => ({
      ...prev,
      route: newOrderIds
    }));
    toast.success('Orden actualizado');
  };

  const handleCreateRoute = async () => {
    if (!selectedMessenger) {
      toast.error('Selecciona un mensajero');
      return;
    }

    if (selectedOrders.length === 0) {
      toast.error('Selecciona al menos un pedido');
      return;
    }

    try {
      setLoading(true);

      const routeData = {
        messenger_id: selectedMessenger,
        order_ids: optimizedRoute ? optimizedRoute.route : selectedOrders,
        route_date: routeDate
      };

      const result = await createRoute(routeData);

      if (result.success) {
        toast.success('Ruta creada exitosamente');
        setSelectedOrders([]);
        setOptimizedRoute(null);
        setSelectedMessenger('');
        loadDispatchData(); // Reload data
      } else {
        toast.error(result.message || 'Error al crear la ruta');
      }
    } catch (err) {
      console.error('Error creando ruta:', err);
      toast.error('Error al crear la ruta');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmHandoff = async (messengerId) => {
    try {
      // Find the messenger's orders from dispatchData
      const messengerData = dispatchData.pending_handoff.by_messenger.find(
        m => m.messenger_id === messengerId
      );

      if (!messengerData || !messengerData.orders || messengerData.orders.length === 0) {
        toast.error('No hay pedidos para confirmar');
        return;
      }

      // Extract order IDs
      const orderIds = messengerData.orders.map(order => order.id);

      const response = await api.post('/logistics/handoff/confirm', {
        messenger_id: messengerId,
        order_ids: orderIds  // Send array of order IDs
      });


      if (response.data.success) {
        toast.success('Handoff confirmado exitosamente');
        loadDispatchData();
      }
    } catch (err) {
      console.error('Error confirmando handoff:', err);
      toast.error('Error al confirmar handoff');
    }
  };

  const handleQuickAssign = async (orderId, messengerId) => {
    if (!messengerId) {
      toast.error('Selecciona un mensajero');
      return;
    }

    try {
      const response = await api.post('/logistics/quick-assign', {
        order_id: orderId,
        messenger_id: messengerId
      });

      if (response.data.success) {
        toast.success('Pedido asignado exitosamente');
        loadDispatchData(); // Reload data
      } else {
        toast.error(response.data.message || 'Error al asignar pedido');
      }
    } catch (err) {
      console.error('Error asignando pedido:', err);
      toast.error('Error al asignar pedido');
    }
  };

  const handleToggleHandoffOrder = (messengerId, orderIdOrIds, selectAll = false) => {
    setSelectedHandoffOrders(prev => {
      const messengerOrders = prev[messengerId] || [];

      // If selectAll is true and orderIdOrIds is an array, select all
      if (selectAll && Array.isArray(orderIdOrIds)) {
        return {
          ...prev,
          [messengerId]: orderIdOrIds
        };
      }

      // Single toggle (existing behavior)
      const orderId = orderIdOrIds;
      const isSelected = messengerOrders.includes(orderId);

      return {
        ...prev,
        [messengerId]: isSelected
          ? messengerOrders.filter(id => id !== orderId)
          : [...messengerOrders, orderId]
      };
    });
  };

  const handleConfirmSelectedHandoff = async (messengerId) => {
    const selectedOrders = selectedHandoffOrders[messengerId] || [];

    if (selectedOrders.length === 0) {
      toast.error('Selecciona al menos un pedido');
      return;
    }

    try {
      const response = await api.post('/logistics/handoff/confirm', {
        messenger_id: messengerId,
        order_ids: selectedOrders
      });


      if (response.data.success) {
        toast.success(`${selectedOrders.length} pedido(s) confirmado(s)`);
        setSelectedHandoffOrders(prev => ({
          ...prev,
          [messengerId]: []
        }));
        loadDispatchData();
      }
    } catch (err) {
      console.error('Error confirmando handoff (SELECCIONADOS):', err);
      toast.error('Error al confirmar handoff');
    }
  };

  const applyDeliveredFilters = () => {
    loadDispatchData(deliveredFilters);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Truck className="text-blue-600" size={28} />
            <h1 className="text-2xl font-bold text-gray-900">Despacho de Ruta o Domicilio</h1>
          </div>
          <p className="text-gray-600">Asignar mensajeros, optimizar rutas y confirmar handoff físico</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('pending_handoff')}
                className={`flex items-center gap-2 px-6 py-3 font-medium border-b-2 transition-colors ${activeTab === 'pending_handoff'
                  ? 'border-amber-600 text-amber-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }`}
              >
                <Users size={18} />
                Por Despachar ({dispatchData.pending_handoff.count})
              </button>

              <button
                onClick={() => setActiveTab('in_delivery')}
                className={`flex items-center gap-2 px-6 py-3 font-medium border-b-2 transition-colors ${activeTab === 'in_delivery'
                  ? 'border-green-600 text-green-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }`}
              >
                <Truck size={18} />
                En Ruta ({dispatchData.in_delivery.count})
              </button>

              <button
                onClick={() => setActiveTab('delivered')}
                className={`flex items-center gap-2 px-6 py-3 font-medium border-b-2 transition-colors ${activeTab === 'delivered'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }`}
              >
                <CheckCircle size={18} />
                Rutas Entregadas ({dispatchData.delivered.count})
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* TAB 1: Por Despachar */}
            {activeTab === 'pending_handoff' && (
              <PendingHandoffTab
                data={dispatchData.pending_handoff.by_messenger}
                messengers={messengers}
                selectedOrders={selectedHandoffOrders}
                onToggleOrder={handleToggleHandoffOrder}
                onConfirmHandoff={handleConfirmHandoff}
                onConfirmSelectedHandoff={handleConfirmSelectedHandoff}
                onReassignMessenger={async (messenger, newMessengerId) => {
                  try {
                    if (messenger.route_id) {
                      // Has route — use route reassignment
                      const response = await api.put(`/routes/${messenger.route_id}/reassign-messenger`, {
                        messenger_id: newMessengerId
                      });
                      if (response.data.success) {
                        toast.success('✅ Mensajero reasignado exitosamente');
                        loadDispatchData();
                      } else {
                        toast.error(response.data.message);
                      }
                    } else {
                      // No route — batch quick-assign each order
                      const orderIds = messenger.orders.map(o => o.id);
                      let success = 0;
                      for (const oid of orderIds) {
                        try {
                          await api.post('/logistics/quick-assign', { order_id: oid, messenger_id: newMessengerId });
                          success++;
                        } catch (e) {
                          console.error('Error reasignando pedido', oid, e);
                        }
                      }
                      if (success > 0) {
                        toast.success(`✅ ${success} pedido(s) reasignado(s)`);
                        loadDispatchData();
                      } else {
                        toast.error('Error al reasignar pedidos');
                      }
                    }
                  } catch (error) {
                    toast.error(error.response?.data?.message || 'Error al reasignar mensajero');
                    console.error(error);
                  }
                }}
              />
            )}

            {/* TAB 3: En Ruta */}
            {activeTab === 'in_delivery' && (
              <InDeliveryTab
                data={dispatchData.in_delivery.by_date}
                messengers={messengers}
                onAddStop={(routeId) => {
                  setSelectedRouteForStops(routeId);
                  setShowStopsModal(true);
                }}
                onLockRoute={async (routeId) => {
                  try {
                    const response = await api.post(`/routes/${routeId}/lock`);
                    if (response.data.success) {
                      toast.success('✅ Ruta consolidada. El mensajero ya puede iniciar entregas.');
                      loadDispatchData(); // Recargar datos
                    } else {
                      toast.error(response.data.message);
                    }
                  } catch (error) {
                    toast.error('Error al consolidar ruta');
                    console.error(error);
                  }
                }}
                onReassignMessenger={async (routeId, newMessengerId) => {
                  try {
                    const response = await api.put(`/routes/${routeId}/reassign-messenger`, {
                      messenger_id: newMessengerId
                    });
                    if (response.data.success) {
                      toast.success('✅ Mensajero reasignado exitosamente');
                      loadDispatchData();
                    } else {
                      toast.error(response.data.message);
                    }
                  } catch (error) {
                    toast.error(error.response?.data?.message || 'Error al reasignar mensajero');
                    console.error(error);
                  }
                }}
                onDeleteStop={async (routeId, stopId) => {
                  if (!window.confirm('¿Estás seguro de eliminar esta parada adicional?')) {
                    return;
                  }

                  try {
                    await api.delete(`/routes/${routeId}/additional-stops/${stopId}`);
                    toast.success('Parada eliminada correctamente');
                    loadDispatchData(); // Refresh dispatch data
                  } catch (error) {
                    console.error('Error deleting stop:', error);
                    toast.error('Error al eliminar la parada');
                  }
                }}
              />
            )}

            {/* TAB 4: Rutas Entregadas */}
            {activeTab === 'delivered' && (
              <DeliveredRoutesTab
                data={dispatchData.delivered.by_messenger}
                filters={deliveredFilters}
                messengers={messengers}
                onFilterChange={setDeliveredFilters}
                onApplyFilters={applyDeliveredFilters}
              />
            )}
          </div>
        </div>
      </div>

      {/* Additional Stops Modal */}
      {showStopsModal && selectedRouteForStops && (
        <AdditionalStopsModal
          isOpen={showStopsModal}
          onClose={() => {
            setShowStopsModal(false);
            setSelectedRouteForStops(null);
            loadDispatchData(); // Reload data after adding stop
          }}
          routeId={selectedRouteForStops}
          isMessenger={false} // Logistics view
        />
      )}

      {/* Geocoding Alerts Popup */}
      {geocodingAlerts && (
        <GeocodingAlertsPopup
          alerts={geocodingAlerts}
          onClose={() => setGeocodingAlerts(null)}
        />
      )}
    </div>
  );
};

// Order Card Component (to avoid React hooks violation in map)
const OrderCard = ({ order, isSelected, messengers, onSelectOrder, onQuickAssign }) => {
  const [quickMessenger, setQuickMessenger] = useState('');

  return (
    <div
      className={`p-4 rounded-lg border-2 transition-all ${isSelected
        ? 'border-blue-500 bg-blue-50'
        : 'border-gray-200 hover:border-gray-300 bg-white'
        }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div
          className="flex-1 cursor-pointer"
          onClick={() => onSelectOrder(order.id)}
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-gray-900">{order.order_number}</span>
            <span className="text-sm px-2 py-0.5 bg-gray-100 rounded text-gray-700">
              ${order.total_amount?.toLocaleString()}
            </span>
          </div>
          <p className="text-sm text-gray-700">{order.customer_name}</p>
          <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
            <MapPin size={14} />
            <span>{order.customer_address}, {order.customer_city}</span>
          </div>
        </div>

        {/* Quick Assign Section */}
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <select
            value={quickMessenger}
            onChange={(e) => setQuickMessenger(e.target.value)}
            className="text-sm px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            onClick={(e) => e.stopPropagation()}
          >
            <option value="">Mensajero...</option>
            {messengers.map(m => (
              <option key={m.id} value={m.id}>{m.full_name}</option>
            ))}
          </select>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onQuickAssign(order.id, quickMessenger);
              setQuickMessenger('');
            }}
            disabled={!quickMessenger}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Asignar
          </button>
          {isSelected && (
            <CheckCircle className="text-blue-600 ml-2" size={20} />
          )}
        </div>
      </div>
    </div>
  );
};

// Sortable Order Item Component
const SortableOrderItem = ({ order, index, totalOrders }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: order.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-3 rounded-lg border-2 ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
        } hover:border-gray-300 transition-all`}
    >
      <div className="flex items-center gap-3">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-2 hover:bg-gray-100 rounded"
        >
          <GripVertical className="w-5 h-5 text-gray-400" />
        </div>

        {/* Sequence Number */}
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">
          {index + 1}
        </div>

        {/* Order Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900">{order.order_number}</span>
            <span className="text-xs px-2 py-0.5 bg-gray-100 rounded text-gray-700">
              ${order.total_amount?.toLocaleString()}
            </span>
          </div>
          <p className="text-sm text-gray-700 truncate">{order.customer_name}</p>
          <div className="flex items-center gap-1 text-xs text-gray-600 mt-0.5">
            <MapPin size={12} />
            <span className="truncate">{order.customer_address}</span>
          </div>
        </div>

        {/* Distance (if available) */}
        {order.distance && (
          <div className="text-xs text-blue-600 font-medium">
            {parseFloat(order.distance).toFixed(1)} km
          </div>
        )}
      </div>
    </div>
  );
};

// TAB 1 COMPONENT: Unassigned Orders
const UnassignedTab = ({
  orders,
  selectedOrders,
  messengers,
  selectedMessenger,
  routeDate,
  optimizedRoute,
  loading,
  optimizing,
  onSelectOrder,
  onSelectAll,
  onMessengerChange,
  onDateChange,
  onOptimize,
  onCreate,
  onQuickAssign,
  onReorder
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = optimizedRoute.route.indexOf(active.id);
      const newIndex = optimizedRoute.route.indexOf(over.id);
      const newOrder = arrayMove(optimizedRoute.route, oldIndex, newIndex);
      onReorder(newOrder);
    }
  };

  // Get optimized orders in correct sequence
  const getOptimizedOrders = () => {
    if (!optimizedRoute) return [];
    return optimizedRoute.route.map(orderId =>
      orders.find(o => o.id === orderId)
    ).filter(Boolean);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left: Orders List or Optimized Route */}
      <div className="lg:col-span-2">
        {optimizedRoute ? (
          // Show Optimized Route (Sortable)
          <>
            <div className="mb-4">
              <h3 className="font-semibold text-gray-900 mb-2">
                🗺️ Ruta Optimizada ({optimizedRoute.route.length} paradas)
              </h3>
              <p className="text-sm text-gray-600">
                Arrastra las paradas para ajustar el orden manualmente
              </p>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={optimizedRoute.route}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                  {getOptimizedOrders().map((order, index) => (
                    <SortableOrderItem
                      key={order.id}
                      order={order}
                      index={index}
                      totalOrders={optimizedRoute.route.length}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            <button
              onClick={() => {
                if (window.confirm('¿Descartar ruta optimizada y volver a selección de pedidos?')) {
                  onCreate._reset?.(); // Optional reset callback
                }
              }}
              className="mt-4 w-full py-2 px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              ← Volver a selección
            </button>
          </>
        ) : (
          // Show Available Orders
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">
                Pedidos Disponibles ({orders.length})
              </h3>
              {orders.length > 0 && (
                <button
                  onClick={onSelectAll}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  {selectedOrders.length === orders.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                </button>
              )}
            </div>

            {orders.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <Package className="mx-auto text-gray-400 mb-3" size={48} />
                <p className="text-gray-600">No hay pedidos disponibles para enrutar</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {orders.map(order => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    isSelected={selectedOrders.includes(order.id)}
                    messengers={messengers}
                    onSelectOrder={onSelectOrder}
                    onQuickAssign={onQuickAssign}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Right: Route Configuration */}
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-4">Configuración de Ruta</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mensajero
              </label>
              <select
                value={selectedMessenger}
                onChange={(e) => onMessengerChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={optimizedRoute !== null}
              >
                <option value="">Seleccionar mensajero</option>
                {messengers.map(m => (
                  <option key={m.id} value={m.id}>{m.full_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha de ruta
              </label>
              <input
                type="date"
                value={routeDate}
                onChange={(e) => onDateChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={optimizedRoute !== null}
              />
            </div>

            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Resumen</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Pedidos:</span>
                  <span className="font-medium">{optimizedRoute ? optimizedRoute.route.length : selectedOrders.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Valor total:</span>
                  <span className="font-medium">
                    ${orders
                      .filter(o => (optimizedRoute ? optimizedRoute.route.includes(o.id) : selectedOrders.includes(o.id)))
                      .reduce((sum, o) => sum + (o.total_amount || 0), 0)
                      .toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Efectivo esperado:</span>
                  <span className="font-medium text-green-600">
                    ${orders
                      .filter(o => (optimizedRoute ? optimizedRoute.route.includes(o.id) : selectedOrders.includes(o.id)) && o.payment_method === 'efectivo')
                      .reduce((sum, o) => sum + (o.total_amount || 0), 0)
                      .toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Pedidos en efectivo:</span>
                  <span className="font-medium">
                    {orders.filter(o => (optimizedRoute ? optimizedRoute.route.includes(o.id) : selectedOrders.includes(o.id)) && o.payment_method === 'efectivo').length}
                  </span>
                </div>
              </div>
            </div>

            {!optimizedRoute ? (
              <button
                onClick={onOptimize}
                disabled={!selectedMessenger || selectedOrders.length === 0 || optimizing}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Navigation size={18} />
                {optimizing ? 'Optimizando...' : 'Optimizar Ruta'}
              </button>
            ) : (
              <button
                onClick={onCreate}
                disabled={loading}
                className="w-full py-3 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-semibold"
              >
                <CheckCircle size={20} />
                {loading ? 'Creando...' : 'Crear Ruta'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// TAB 2 COMPONENT: Pending Handoff
const PendingHandoffTab = ({ data, messengers = [], selectedOrders, onToggleOrder, onConfirmHandoff, onConfirmSelectedHandoff, onReassignMessenger }) => {
  const [reassigningMessengerId, setReassigningMessengerId] = useState(null);
  const [newMessengerId, setNewMessengerId] = useState('');

  return (
    <div className="space-y-4">
      {data.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Users className="mx-auto text-gray-400 mb-3" size={48} />
          <p className="text-gray-600">No hay pedidos pendientes de handoff</p>
        </div>
      ) : (
        data.map(messenger => {
          const messengerSelected = selectedOrders[messenger.messenger_id] || [];
          const hasSelected = messengerSelected.length > 0;

          return (
            <div key={messenger.messenger_id} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between border-b pb-3 mb-3">
                <div className="flex items-center gap-2">
                  <User size={18} />
                  <span className="font-semibold text-gray-900">{messenger.messenger_name}</span>
                  <span className="text-sm text-gray-600">({messenger.orders.length} pedidos)</span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {/* Botón Reasignar Mensajero */}
                  {onReassignMessenger && (
                    reassigningMessengerId === messenger.messenger_id ? (
                      <div className="flex items-center gap-2">
                        <select
                          value={newMessengerId}
                          onChange={(e) => setNewMessengerId(e.target.value)}
                          className="text-sm px-2 py-1.5 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-amber-50"
                          autoFocus
                        >
                          <option value="">Seleccionar mensajero...</option>
                          {messengers.filter(m => m.id !== messenger.messenger_id).map(m => (
                            <option key={m.id} value={m.id}>{m.full_name}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => {
                            if (newMessengerId) {
                              onReassignMessenger(messenger, newMessengerId);
                              setReassigningMessengerId(null);
                              setNewMessengerId('');
                            } else {
                              toast.error('Selecciona un mensajero');
                            }
                          }}
                          disabled={!newMessengerId}
                          className="px-3 py-1.5 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium"
                        >
                          Confirmar
                        </button>
                        <button
                          onClick={() => { setReassigningMessengerId(null); setNewMessengerId(''); }}
                          className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm font-medium"
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setReassigningMessengerId(messenger.messenger_id)}
                        className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded hover:bg-amber-200 flex items-center gap-1.5 text-sm font-medium"
                        title="Reasignar mensajero de esta ruta"
                      >
                        <RefreshCw size={16} />
                        Reasignar
                      </button>
                    )
                  )}

                  {/* Botón para seleccionar todos los checkboxes */}
                  <button
                    onClick={() => {
                      const allOrderIds = messenger.orders.map(o => o.id);
                      onToggleOrder(messenger.messenger_id, allOrderIds, true);
                    }}
                    className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center gap-1.5 text-sm"
                  >
                    <Package size={16} />
                    Seleccionar Todos
                  </button>

                  {hasSelected && (
                    <button
                      onClick={() => onConfirmSelectedHandoff(messenger.messenger_id)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                      <CheckCircle size={18} />
                      Confirmar Seleccionados ({messengerSelected.length})
                    </button>
                  )}
                  <button
                    onClick={() => onConfirmHandoff(messenger.messenger_id)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                  >
                    <CheckCircle size={18} />
                    Confirmar Todos
                  </button>
                </div>
              </div>

              <DetailedOrdersTable
                orders={messenger.orders}
                showHandoffCheckbox={true}
                selectedOrders={messengerSelected}
                onToggleOrder={(orderId) => onToggleOrder(messenger.messenger_id, orderId)}
              />
            </div>
          );
        })
      )}
    </div>
  );
};

// TAB 3 COMPONENT: In Delivery
const InDeliveryTab = ({ data, messengers = [], onAddStop, onLockRoute, onReassignMessenger, onDeleteStop }) => {
  const [reassigningRouteId, setReassigningRouteId] = useState(null);
  const [newMessengerId, setNewMessengerId] = useState('');
  const [editingStopId, setEditingStopId] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        No hay pedidos en ruta
      </div>
    );
  }

  const handleEditStart = (stop) => {
    setEditingStopId(stop.id);
    setEditFormData({
      stop_type: stop.stop_type || 'other',
      address: stop.address || '',
      description: stop.description || '',
      payment_amount: stop.payment_amount || 0
    });
  };

  const handleEditCancel = () => {
    setEditingStopId(null);
    setEditFormData({});
  };

  const handleEditSave = async (routeId, stopId) => {
    if (!editFormData.address || !editFormData.address.trim()) {
      toast.error('La dirección es obligatoria');
      return;
    }

    try {
      await api.put(`/routes/${routeId}/additional-stops/${stopId}`, editFormData);
      toast.success('Parada actualizada correctamente');
      setEditingStopId(null);
      setEditFormData({});
      // Reload data - passed as onRefresh prop
      window.location.reload(); // temporary - should use proper refresh
    } catch (error) {
      console.error('Error updating stop:', error);
      toast.error(error.response?.data?.message || 'Error al actualizar la parada');
    }
  };

  return (
    <div className="space-y-6">
      {data.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No hay rutas activas
        </div>
      ) : (
        data.map(dateGroup => (
          <div key={dateGroup.date} className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center gap-2">
              <Calendar size={20} />
              {new Date(dateGroup.date).toLocaleDateString('es-CO', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </h3>

            <div className="space-y-3">
              {dateGroup.messengers.map(messenger => (
                <div key={messenger.messenger_id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="font-semibold text-gray-900">{messenger.messenger_name}</div>
                      <div className="text-sm text-gray-600">{messenger.total_orders} pedidos</div>

                      {/* Badge de Preruta vs En Reparto */}
                      {!messenger.is_locked ? (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-bold rounded uppercase">
                          Preruta (Abierta)
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded uppercase">
                          En Reparto (Cerrada)
                        </span>
                      )}

                      {messenger.pending_stops > 0 && (
                        <div className="text-sm bg-purple-100 text-purple-700 px-2 py-0.5 rounded flex items-center gap-1">
                          <MapPin size={14} />
                          {messenger.pending_stops} parada{messenger.pending_stops !== 1 ? 's' : ''} adicional{messenger.pending_stops !== 1 ? 'es' : ''}
                        </div>
                      )}
                    </div>
                    {messenger.route_id && (
                      <div className="flex gap-2 flex-wrap">
                        {/* Botón Reasignar Mensajero - Solo si ruta ABIERTA */}
                        {!messenger.is_locked && onReassignMessenger && (
                          reassigningRouteId === messenger.route_id ? (
                            <div className="flex items-center gap-2">
                              <select
                                value={newMessengerId}
                                onChange={(e) => setNewMessengerId(e.target.value)}
                                className="text-sm px-2 py-1.5 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-amber-50"
                                autoFocus
                              >
                                <option value="">Seleccionar mensajero...</option>
                                {messengers.filter(m => m.id !== messenger.messenger_id).map(m => (
                                  <option key={m.id} value={m.id}>{m.full_name}</option>
                                ))}
                              </select>
                              <button
                                onClick={() => {
                                  if (newMessengerId) {
                                    onReassignMessenger(messenger.route_id, newMessengerId);
                                    setReassigningRouteId(null);
                                    setNewMessengerId('');
                                  } else {
                                    toast.error('Selecciona un mensajero');
                                  }
                                }}
                                disabled={!newMessengerId}
                                className="px-3 py-1.5 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium"
                              >
                                Confirmar
                              </button>
                              <button
                                onClick={() => { setReassigningRouteId(null); setNewMessengerId(''); }}
                                className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm font-medium"
                              >
                                Cancelar
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setReassigningRouteId(messenger.route_id)}
                              className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded hover:bg-amber-200 flex items-center gap-1.5 text-sm font-medium"
                              title="Reasignar mensajero de esta ruta"
                            >
                              <RefreshCw size={16} />
                              Reasignar
                            </button>
                          )
                        )}

                        {/* Botón Agregar Parada - Solo si ruta ABIERTA */}
                        <button
                          onClick={() => onAddStop(messenger.route_id)}
                          disabled={messenger.is_locked}
                          className={`px-3 py-1.5 rounded flex items-center gap-1.5 text-sm font-medium ${messenger.is_locked
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                            }`}
                          title={messenger.is_locked ? 'La ruta está cerrada' : 'Agregar parada adicional'}
                        >
                          <Plus size={16} />
                          Agregar Parada
                        </button>

                        {/* Botón Consolidar Ruta - Solo si ruta ABIERTA */}
                        {!messenger.is_locked && onLockRoute && (
                          <button
                            onClick={() => onLockRoute(messenger.route_id)}
                            className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1.5 text-sm font-medium"
                            title="Consolidar ruta para que el mensajero pueda iniciar"
                          >
                            <Lock size={16} />
                            Consolidar Ruta
                          </button>
                        )}

                        {messenger.is_locked && (
                          <div className="px-3 py-1.5 bg-green-100 text-green-800 rounded flex items-center gap-1.5 text-sm font-semibold">
                            <CheckCircle size={16} />
                            Ruta Cerrada
                          </div>
                        )}

                        {/* Botón Imprimir Hoja de Ruta */}
                        <button
                          onClick={() => printDispatchRouteSheet(messenger, dateGroup.date)}
                          className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center gap-1.5 text-sm font-medium"
                          title="Imprimir hoja de ruta"
                        >
                          <Printer size={16} />
                          Imprimir
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Additional Stops Section */}
                  {messenger.additional_stops && messenger.additional_stops.length > 0 && (
                    <div className="mb-3">
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <div className="font-medium text-purple-900 mb-2 flex items-center gap-2">
                          <MapPin size={16} className="text-purple-600" />
                          Paradas Adicionales Programadas
                        </div>
                        <div className="space-y-2">
                          {messenger.additional_stops.map((stop, idx) => {
                            const stopTypeEmoji = {
                              pickup: '📦',
                              delivery: '🚚',
                              errand: '📝',
                              other: '📍'
                            };

                            return (
                              <div key={stop.id} className="bg-white rounded border border-purple-200 p-2 text-sm">
                                {editingStopId === stop.id ? (
                                  // Edit Mode
                                  <div>
                                    <div className="mb-2">
                                      <label className="block text-xs text-gray-600 mb-1">Tipo</label>
                                      <select
                                        value={editFormData.stop_type}
                                        onChange={(e) => setEditFormData({ ...editFormData, stop_type: e.target.value })}
                                        className="w-full border rounded px-2 py-1 text-sm"
                                      >
                                        <option value="pickup">📦 Recogida</option>
                                        <option value="delivery">🚚 Entrega</option>
                                        <option value="errand">📝 Diligencia</option>
                                        <option value="other">📍 Otro</option>
                                      </select>
                                    </div>
                                    <div className="mb-2">
                                      <label className="block text-xs text-gray-600 mb-1">Dirección</label>
                                      <input
                                        type="text"
                                        value={editFormData.address}
                                        onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                                        className="w-full border rounded px-2 py-1 text-sm"
                                        placeholder="Dirección"
                                      />
                                    </div>
                                    <div className="mb-2">
                                      <label className="block text-xs text-gray-600 mb-1">Monto a cobrar</label>
                                      <input
                                        type="number"
                                        value={editFormData.payment_amount}
                                        onChange={(e) => setEditFormData({ ...editFormData, payment_amount: e.target.value })}
                                        className="w-full border rounded px-2 py-1 text-sm"
                                        placeholder="0"
                                      />
                                    </div>
                                    <div className="mb-2">
                                      <label className="block text-xs text-gray-600 mb-1">Descripción</label>
                                      <textarea
                                        value={editFormData.description}
                                        onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                                        className="w-full border rounded px-2 py-1 text-sm"
                                        rows="2"
                                        placeholder="Descripción (opcional)"
                                      />
                                    </div>
                                    <div className="flex gap-2 justify-end">
                                      <button
                                        onClick={handleEditCancel}
                                        className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded"
                                      >
                                        Cancelar
                                      </button>
                                      <button
                                        onClick={() => handleEditSave(messenger.route_id, stop.id)}
                                        className="px-3 py-1 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded"
                                      >
                                        Guardar
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  // View Mode
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-base">{stopTypeEmoji[stop.stop_type] || '📍'}</span>
                                        <span className="font-medium text-purple-900 capitalize">{stop.stop_type || 'Parada'}</span>
                                        {stop.payment_amount > 0 && (
                                          <span className="text-purple-700 font-semibold">
                                            ${parseFloat(stop.payment_amount).toLocaleString()}
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-gray-700 flex items-start gap-1">
                                        <MapPin size={12} className="mt-0.5 text-gray-400 flex-shrink-0" />
                                        <span>{stop.address || 'Sin dirección'}</span>
                                      </div>
                                      {stop.description && (
                                        <div className="text-gray-600 text-xs mt-1 italic">
                                          {stop.description}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex gap-1">
                                      <button
                                        onClick={() => handleEditStart(stop)}
                                        disabled={messenger.is_locked === 1}
                                        className={`p-1 rounded transition-colors ${messenger.is_locked === 1
                                          ? 'text-gray-300 cursor-not-allowed'
                                          : 'text-blue-600 hover:bg-blue-50'
                                          }`}
                                        title={messenger.is_locked === 1 ? 'No se puede editar - Ruta cerrada' : 'Editar parada'}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="18"
                                          height="18"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                        </svg>
                                      </button>
                                      <button
                                        onClick={() => onDeleteStop(messenger.route_id, stop.id)}
                                        disabled={messenger.is_locked === 1}
                                        className={`p-1 rounded transition-colors ${messenger.is_locked === 1
                                          ? 'text-gray-300 cursor-not-allowed'
                                          : 'text-red-600 hover:bg-red-50'
                                          }`}
                                        title={messenger.is_locked === 1 ? 'No se puede eliminar - Ruta cerrada' : 'Eliminar parada'}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="18"
                                          height="18"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <polyline points="3 6 5 6 21 6"></polyline>
                                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                          <line x1="10" y1="11" x2="10" y2="17"></line>
                                          <line x1="14" y1="11" x2="14" y2="17"></line>
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  <DetailedOrdersTable orders={messenger.orders} />
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// TAB 4 COMPONENT: Delivered Routes
const DeliveredRoutesTab = ({ data, filters, messengers, onFilterChange, onApplyFilters }) => {
  return (
    <div>
      {/* Filters */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-gray-900 mb-4">Filtros</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Desde</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => onFilterChange({ ...filters, date_from: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Hasta</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => onFilterChange({ ...filters, date_to: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Mensajero</label>
            <select
              value={filters.messenger_id}
              onChange={(e) => onFilterChange({ ...filters, messenger_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Todos los mensajeros</option>
              {messengers.map(m => (
                <option key={m.id} value={m.id}>{m.full_name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={onApplyFilters}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Aplicar
            </button>
          </div>
        </div>
      </div>

      {/* Delivered Routes */}
      <div className="space-y-4">
        {data.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <CheckCircle className="mx-auto text-gray-400 mb-3" size={48} />
            <p className="text-gray-600">No hay rutas entregadas en este rango de fechas</p>
          </div>
        ) : (
          data.map(messenger => (
            <div key={messenger.messenger_id} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                <div>
                  <h3 className="font-bold text-lg text-gray-900">{messenger.messenger_name}</h3>
                  <p className="text-sm text-gray-600">
                    {messenger.total_orders} entregas • ${messenger.total_collected.toLocaleString()} recaudado
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {messenger.routes.map((route, idx) => (
                  <details key={idx} className="bg-gray-50 rounded-lg border border-gray-200">
                    <summary className="cursor-pointer p-4 hover:bg-gray-100 rounded-lg font-medium">
                      <div className="flex items-center justify-between">
                        <span>{new Date(route.date).toLocaleDateString('es-CO')}</span>
                        <div className="text-sm text-gray-600">
                          {route.total_delivered} pedidos • ${route.total_collected.toLocaleString()}
                        </div>
                      </div>
                    </summary>
                    <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {route.orders.map(order => (
                        <div key={order.id} className="text-sm p-2 bg-white rounded border border-gray-200">
                          <div className="font-medium">{order.order_number}</div>
                          <div className="text-gray-600 truncate">{order.customer_name}</div>
                          <div className="text-green-600 font-medium">${order.payment_collected?.toLocaleString() || 0}</div>
                        </div>
                      ))}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RouteCreationPage;
